import * as React from 'react';
import {connect} from 'react-redux';

import classnames from 'classnames';
import moment from 'moment';
import type Moment from 'moment';

// @ts-expect-error
import {groupCalendarItemsByDay} from 'nutshell-core/group-calendar-items-by-day';
import {Session} from 'nutshell-core/session';

import {CalendarWrapper} from 'shells/calendar/calendar-wrapper';
// @ts-expect-error
import {ActivityIcon} from 'shells/icon/svg/activity-icon';
import {
    VIEW_ACTIVITIES_TOUR_CALENDAR_BUTTON_ID,
    VIEW_ACTIVITIES_TOUR_CALENDAR_ID,
} from 'shells/tours/tours';
import {useNutshellTour} from 'shells/tours/use-nutshell-tour';

import {openComposerWithId} from '../../../email/email-composer-actions';

import type {NutshellState} from '../../../../store';

// @ts-expect-error
import * as actions from '../../dashboard-actions';

// @ts-expect-error
import {HIDDEN} from '../activity-constants';
import {StickyHeader} from '../sticky-header';

import * as selectors from './activities-selectors';
import {ActivityFilters} from './activity-filters';
import {ActivityList} from './activity-list';
// @ts-expect-error
import {ListToggleHeader} from './list-toggle-header';
import {OverdueActivities} from './overdue-activities';

import styles from './activities.module.css';

const mapStateToProps = function (state: NutshellState): StateProps {
    return {
        userId: Session.getSessionStateUserId(state),
        monthlyActivities: selectors.getCalendarItems(state),
        overdueActivities: selectors.getOverdueItems(state),
        overdueListIsExpanded: selectors.getOverdueListIsExpanded(state),
        overdueListIsRequesting: selectors.getOverdueListIsRequesting(state),
        totalOverdueCount: selectors.getTotalOverdueCount(state),
        displayDate: selectors.getDisplayDate(state),
        formattedDisplayDateForList: selectors.getFormattedDisplayDateForList(state),
        calendarDisplayMode: selectors.getCalendarDisplayMode(state),
        deparamedFilters: selectors.getDeparamedFilters(state),
        overdueSortDirection: selectors.getOverdueSortDirection(state),
    };
};

// @ts-expect-error
function mapDispatchToProps(dispatch): DispatchProps {
    return {
        onDisplayDateChange: (date) => {
            dispatch(actions.updateActivityDisplayDate(date));
        },
        onCalendarDisplayModeToggle: () => {
            dispatch(actions.updateActivityCalendarDisplayMode());
        },
        onOverdueSortDirectionToggle: (newDirection: 'asc' | 'desc') => {
            dispatch(actions.updateOverdueSortDirection(newDirection));
            dispatch(actions.fetchOverdueActivities());
        },
        getActivitiesForDate: (date) => {
            dispatch(actions.fetchActivitiesByDate(date));
        },
        getActivitiesForMonth: () => {
            dispatch(actions.fetchActivitiesForMonth());
        },
        getOverdueActivities: () => {
            dispatch(actions.fetchOverdueActivities());
        },
        onToggleOverdueClick: () => {
            dispatch(actions.toggleActivityOverdueListIsExpanded());
        },
        onStartEmailToParticipant: (entityId: string) => {
            dispatch(openComposerWithId(entityId));
        },
    };
}

type OwnProps = Record<any, any>;

type StateProps = {
    userId: string | null | undefined;
    monthlyActivities: any[];
    overdueActivities: any[];
    overdueListIsExpanded: boolean;
    overdueListIsRequesting: boolean;
    totalOverdueCount: number;
    displayDate: Date;
    formattedDisplayDateForList: string;
    calendarDisplayMode: string;
    deparamedFilters: any;
    overdueSortDirection: 'asc' | 'desc';
};

type DispatchProps = {
    onDisplayDateChange: (date: Date) => void;
    onCalendarDisplayModeToggle: () => void;
    onOverdueSortDirectionToggle: (newDirection: 'asc' | 'desc') => void;
    getActivitiesForDate: (date: Date) => void;
    getActivitiesForMonth: () => void;
    getOverdueActivities: () => void;
    onToggleOverdueClick: () => void;
    onStartEmailToParticipant: (entityId: string) => void;
};

type Props = OwnProps & StateProps & DispatchProps;

function CalendarToggleTourWatcher({calendarDisplayMode}: {calendarDisplayMode: string}) {
    const {progressTour} = useNutshellTour();
    const hasProgressed = React.useRef(false);

    React.useEffect(() => {
        if (hasProgressed.current) return;

        if (calendarDisplayMode !== HIDDEN) {
            progressTour();
            hasProgressed.current = true;
        }
    }, [calendarDisplayMode, progressTour]);

    return null;
}

export class DashboardActivitiesComponent extends React.Component<Props> {
    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Fetch new activities
        if (nextProps.userId !== this.props.userId) {
            this.syncActivities();
        }

        // No reason to also get overdue activities again, as that request in unaffected
        // by the display date
        if (nextProps.displayDate !== this.props.displayDate) {
            this.props.getActivitiesForMonth();
        }

        // Always get overdue activities when the filters change, as they're affected
        // by these filters as well. We do not need to get activities for month, because
        // we client-side filter those bad boys.
        if (
            nextProps.deparamedFilters !== this.props.deparamedFilters &&
            nextProps.deparamedFilters.status !== 'logged'
        ) {
            this.props.getOverdueActivities();
        }
    }

    componentDidMount() {
        this.props.getActivitiesForDate(this.props.displayDate);

        if (this.props.deparamedFilters.status !== 'logged') {
            this.syncActivities();
        } else {
            this.props.getActivitiesForMonth();
        }
    }

    render() {
        const {
            totalOverdueCount,
            overdueListIsRequesting,
            overdueListIsExpanded,
            deparamedFilters,
            displayDate,
        } = this.props;

        const momentDisplayDate = moment(displayDate);
        const quickViews = groupCalendarItemsByDay(this.props.monthlyActivities);
        const isFilteringByLogged = deparamedFilters.status === 'logged';
        const calendarIsShown = this.props.calendarDisplayMode !== HIDDEN;

        return (
            <div className={styles.container}>
                <div className={styles.filters}>
                    <ActivityFilters filter={this.props.deparamedFilters} />
                    <CalendarToggleTourWatcher
                        calendarDisplayMode={this.props.calendarDisplayMode}
                    />
                    <button
                        id={VIEW_ACTIVITIES_TOUR_CALENDAR_BUTTON_ID}
                        onClick={this.props.onCalendarDisplayModeToggle}
                        className={classnames(
                            'ui-btn--icon',
                            calendarIsShown
                                ? styles['calendar-icon-container-open']
                                : styles['calendar-icon-container']
                        )}
                    >
                        <ActivityIcon
                            className={styles['calendar-icon']}
                            size={21}
                            data-tip='Toggle calendar'
                        />
                    </button>
                </div>
                {calendarIsShown ? (
                    <div
                        id={VIEW_ACTIVITIES_TOUR_CALENDAR_ID}
                        className={styles['calendar-container']}
                    >
                        <CalendarWrapper
                            displayMode={this.props.calendarDisplayMode}
                            isLightMode={true}
                            onDateClick={this.handleDisplayDateChange}
                            changingMonthUpdatesSelectedDate={true}
                            quickViews={quickViews}
                            momentDisplayDate={momentDisplayDate}
                        />
                    </div>
                ) : undefined}
                {this.props.userId ? (
                    <div className={styles['activity-lists-container']}>
                        {(totalOverdueCount > 0 || overdueListIsRequesting) &&
                        !isFilteringByLogged ? (
                            <ListToggleHeader
                                isLoading={this.props.overdueListIsRequesting}
                                loadingText='Checking for unlogged activities…'
                                onClick={this.props.onToggleOverdueClick}
                                text='Unlogged activities'
                                count={this.props.totalOverdueCount}
                                isOpen={this.props.overdueListIsExpanded}
                                sortDirection={this.props.overdueSortDirection}
                                onSortToggle={this.handleSortToggle}
                            />
                        ) : undefined}
                        {overdueListIsExpanded && totalOverdueCount > 0 && !isFilteringByLogged ? (
                            <OverdueActivities
                                userId={this.props.userId}
                                activities={this.props.overdueActivities}
                                onStartEmailToParticipant={this.props.onStartEmailToParticipant}
                                onStartCallToParticipant={this.handleStartCallToParticipant}
                            />
                        ) : undefined}
                        <StickyHeader>{this.props.formattedDisplayDateForList}</StickyHeader>
                        <ActivityList
                            userId={this.props.userId}
                            onStartEmailToParticipant={this.props.onStartEmailToParticipant}
                            onStartCallToParticipant={this.handleStartCallToParticipant}
                        />
                    </div>
                ) : undefined}
            </div>
        );
    }

    // @ts-expect-error
    handleDisplayDateChange = (momentDate: Moment) => {
        // @ccain TODO - change this to use unix seconds
        this.props.onDisplayDateChange(momentDate.toDate());
    };

    handleSortToggle = () => {
        const newDirection = this.props.overdueSortDirection === 'asc' ? 'desc' : 'asc';
        this.props.onOverdueSortDirectionToggle(newDirection);
    };

    syncActivities = () => {
        this.props.getActivitiesForMonth();
        this.props.getOverdueActivities();
    };

    // @ts-expect-error
    handleWeekScroll = (date: Moment) => {
        this.handleDisplayDateChange(date);
    };

    handleStartCallToParticipant = ({
        entityId,
        phoneNumber,
        anchorElementId,
    }: {
        entityId: string;
        phoneNumber: {
            E164: string;
            numberFormatted: string;
        };
        anchorElementId: string;
    }) => {
        $(window).trigger(Peep.events.showReactClickToCallPopover, {
            entityId,
            anchorElementId,
            rawPhoneNumber: phoneNumber.E164,
            formattedPhoneNumber: phoneNumber.numberFormatted,
        });
    };
}

// @ts-expect-error
export const DashboardActivities = connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps,
    mapDispatchToProps
)(DashboardActivitiesComponent);
